import OnboardingLayout from "component/auth/OnboardingLayout";
import UploadInputField from "component/auth/onboarding/UploadInputField";
import SampleDocumentModal from "component/auth/onboarding/SampleDocumentModal";
import { identificationSampleDocuments } from "component/auth/onboarding/onboardingSampleDocuments";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import React, { useCallback, useEffect, useState, Fragment } from "react";
import { useSelector } from "react-redux";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import { useLocation, useNavigate } from "react-router-dom";
import {
    handleAddBusinessDirector,
    handleFetchBusinessById,
    handleFetchKybCountries,
    handleUploadOnboardingDocument,
} from "api/onboarding";
import { InfoCircle } from "iconsax-reactjs";
import { Country } from "country-state-city";
import { cn } from "lib/utils";

const steps = [
    { title: "Business information" },
    { title: "Business verification" },
    { title: "Business ownership", subtitles: ["Control person", "Directors"] },
];

const meansOfIDOptions = [
    { label: "Passport", value: "Passport" },
    { label: "National ID", value: "National ID" },
    { label: "Driver's License", value: "Driver's License" },
];

const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
];

const normalizeCountriesResponse = (responseBody) =>
    (Array.isArray(responseBody?.data) && responseBody.data) ||
    (Array.isArray(responseBody?.data?.countries) && responseBody.data.countries) ||
    (Array.isArray(responseBody?.countries) && responseBody.countries) ||
    (Array.isArray(responseBody) && responseBody) ||
    [];

const toApiIdentificationType = (type) =>
    ({
        Passport: "passport",
        "National ID": "nationalId",
        "Driver's License": "driversLicense",
    })[type] || type;

const extractHouseNumber = (street = "") => {
    const match = street.trim().match(/^(\d+[A-Za-z-]?)/);
    return match ? match[1] : "";
};

const getRegisteredBusinessCountryCode = (business = {}) => {
    if (business?.businessCountry?.code) return String(business.businessCountry.code).toUpperCase();
    if (business?.countryCode) return String(business.countryCode).toUpperCase();
    const countryName = business?.businessCountry?.name || business?.country;
    return (
        Country.getAllCountries().find(
            (country) =>
                country.name.toLowerCase() ===
                String(countryName || "")
                    .trim()
                    .toLowerCase()
        )?.isoCode || ""
    );
};

const getRegisteredBusinessCountryName = (business = {}) =>
    business?.businessCountry?.name || business?.country || "";

const normalizeCountryName = (countryName) =>
    String(countryName || "")
        .trim()
        .toLowerCase();

const isNigerianCountry = (countryCode) => String(countryCode || "").toUpperCase() === "NG";
const isValidBvn = (value) => /^\d{11}$/.test(String(value || ""));
const normalizeOwnershipInput = (value) =>
    String(value || "")
        .replace(/[^\d.]/g, "")
        .replace(/(\..*)\./g, "$1");

const parseOwnershipShare = (value) => {
    const ownership = Number(String(value ?? "").replace(/[,%\s]/g, ""));
    return Number.isFinite(ownership) ? ownership : 0;
};

const isOwnershipShareValid = (value) => {
    if (!String(value ?? "").trim()) return false;
    const ownership = parseOwnershipShare(value);
    return ownership >= 25 && ownership <= 100;
};

const formatDateValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getMaxDateOfBirth = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return formatDateValue(date);
};

const initialDirectorForm = {
    firstName: "",
    lastName: "",
    email: "",
    phoneCountry: "",
    phoneNumber: "",
    title: "",
    ownershipShare: "",
    bvn: "",
    gender: "",
    dateOfBirth: "",
    meansOfID: "Passport",
    identificationNumber: "",
    idDocumentFront: null,
    idDocumentFrontUrl: "",
    idDocumentFrontKey: "",
    idDocumentBack: null,
    idDocumentBackUrl: "",
    idDocumentBackKey: "",
    streetAddress: "",
    city: "",
    state: "",
    postalCode: "",
};

const fromApiMeansOfId = (type) =>
    ({
        passport: "Passport",
        nationalid: "National ID",
        national_id: "National ID",
        driverslicense: "Driver's License",
        drivers_license: "Driver's License",
    })[
        String(type || "")
            .replace(/\s/g, "")
            .toLowerCase()
    ] || "Passport";

const mapDirectorToForm = (d, defaultPhoneCountry = "", phoneCountryOptions = []) => {
  if (!d) return null;

  const meansOfID = fromApiMeansOfId(
    d.meanOfIdentificationType || d.meansOfIdentification || d.meansOfID || "Passport"
  );

  const frontUrl =
    d.idDocumentFront?.url ||
    d.meanOfIdentificationUrl ||
    d.identificationDocument?.url ||
    d.idDocumentFrontUrl ||
    (typeof d.idDocumentFront === "string" ? d.idDocumentFront : "") ||
    "";
  const frontKey =
    d.idDocumentFront?.key ||
    d.meanOfIdentificationKey ||
    d.identificationDocument?.key ||
    d.idDocumentFrontKey ||
    "Identification front";

  const backUrl =
    d.idDocumentBack?.url ||
    d.idDocumentBackUrl ||
    (typeof d.idDocumentBack === "string" ? d.idDocumentBack : "") ||
    "";
  const backKey =
    d.idDocumentBack?.key ||
    d.idDocumentBackKey ||
    "Identification back";

  let phoneCountry = d.phoneCountry || defaultPhoneCountry || "";
  let rawPhone = String(d.phoneNumber || "").trim();

  if (rawPhone.startsWith("+") && phoneCountryOptions.length > 0) {
    const matchingCountry = [...phoneCountryOptions]
      .sort((a, b) => b.dialCode.length - a.dialCode.length)
      .find((option) => rawPhone.startsWith(option.dialCode));
    if (matchingCountry) {
      phoneCountry = matchingCountry.code;
      rawPhone = rawPhone.slice(matchingCountry.dialCode.length);
    }
  } else if (rawPhone.startsWith("+")) {
    rawPhone = rawPhone.replace(/^\+/, "");
  }

  return {
    firstName: d.firstName || "",
    lastName: d.lastName || "",
    email: d.email || "",
    phoneCountry,
    phoneNumber: rawPhone,
    title: d.title || "",
    ownershipShare: String(d.percentageOwnership || d.ownershipShare || ""),
    bvn: d.bvnNumber || d.bvn || "",
    gender: String(d.gender || "").toLowerCase(),
    dateOfBirth: String(d.dateOfBirth || "").slice(0, 10),
    meansOfID,
    identificationNumber:
      d.idNumber || d.meanOfIdentificationNumber || d.identificationNumber || "",
    idDocumentFront: frontUrl
      ? {
        name: frontKey,
        size: 0,
        url: frontUrl,
        key: frontKey,
      }
      : null,
    idDocumentFrontUrl: frontUrl,
    idDocumentFrontKey: frontKey,
    idDocumentBack: backUrl
      ? {
        name: backKey,
        size: 0,
        url: backUrl,
        key: backKey,
      }
      : null,
    idDocumentBackUrl: backUrl,
    idDocumentBackKey: backKey,
    streetAddress:
      d.address?.street ||
      d.residentialAddress?.street ||
      d.residentialAddress?.streetAddress ||
      d.streetAddress ||
      "",
    city: d.address?.city || d.residentialAddress?.city || d.city || "",
    state: d.address?.state || d.residentialAddress?.state || d.state || "",
    postalCode:
      d.address?.zipCode ||
      d.residentialAddress?.postalCode ||
      d.residentialAddress?.zipCode ||
      d.postalCode ||
      "",
    targetId: d.targetId || d._id || d.id || d.email || "",
  };
};

const AddDirectors = ({ currentStep = 2, onBack, isUpdating, handleUpdateMoreData, prefillData }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary =
        new URLSearchParams(location.search).get("returnTo") === "summary";
    const currentBusiness = useSelector((state) => state);
    const businessId = currentBusiness.businessid || currentBusiness.business?._id;
    const reduxBusinessCountryCode = getRegisteredBusinessCountryCode(currentBusiness.business);
    const reduxBusinessCountryName = getRegisteredBusinessCountryName(currentBusiness.business);
    const [businessCountryCode, setBusinessCountryCode] = useState(reduxBusinessCountryCode);
    const [businessCountryName, setBusinessCountryName] = useState(reduxBusinessCountryName);
    const businessRequiresBvn = isNigerianCountry(businessCountryCode);
    const [errorMessage, setErrorMessage] = useState("");
    const [identificationError, setIdentificationError] = useState("");
    const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(Boolean(isUpdating));
    const [directors, setDirectors] = useState([]);
    const [newDirector, setNewDirector] = useState({
        ...initialDirectorForm,
        phoneCountry: businessCountryCode,
    });
    const [phoneCountryOptions, setPhoneCountryOptions] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [phoneCountryTouched, setPhoneCountryTouched] = useState(false);
    const [frontDocumentUpload, setFrontDocumentUpload] = useState({
        loading: false,
        progress: 0,
        uploaded: false,
    });
    const [backDocumentUpload, setBackDocumentUpload] = useState({
        loading: false,
        progress: 0,
        uploaded: false,
    });
    const savedDirectorsForOwnership = shouldReturnToSummary ? directors.slice(1) : directors;
    const savedDirectorsOwnershipTotal = savedDirectorsForOwnership.reduce(
        (total, director) => total + parseOwnershipShare(director.ownershipShare),
        0
    );
    const currentOwnershipShare = parseOwnershipShare(newDirector.ownershipShare);
    const ownershipExceedsLimit =
        Boolean(newDirector.ownershipShare) &&
        savedDirectorsOwnershipTotal + currentOwnershipShare > 100;
    const ownershipBelowMinimum = Boolean(newDirector.ownershipShare) && currentOwnershipShare < 25;
    const ownershipAboveMaximum =
        Boolean(newDirector.ownershipShare) && currentOwnershipShare > 100;
    const ownershipErrorMessage = ownershipBelowMinimum
        ? "Ownership share cannot be less than 25%."
        : ownershipAboveMaximum
          ? "Ownership share cannot be more than 100%."
          : ownershipExceedsLimit
            ? "Total director ownership cannot exceed 100%."
            : "";
    const ownershipHasError = Boolean(ownershipErrorMessage);

    const fetchDirectors = useCallback(async () => {
        try {
            const data = await handleFetchBusinessById(businessId);
            const business = data?.business || data;
            const fetchedBusinessCountryCode = getRegisteredBusinessCountryCode(business);
            const fetchedBusinessCountryName = getRegisteredBusinessCountryName(business);
            if (fetchedBusinessCountryCode) {
                setBusinessCountryCode(fetchedBusinessCountryCode);
            }
            if (fetchedBusinessCountryName) {
                setBusinessCountryName(fetchedBusinessCountryName);
            }
            const apiDirectors = business?.directors || [];

            const mappedDirectors = apiDirectors.map((d) => ({
                firstName: d.firstName || "",
                lastName: d.lastName || "",
                email: d.email || "",
                phoneCountry:
                    d.phoneCountry || fetchedBusinessCountryCode || businessCountryCode || "",
                phoneNumber: d.phoneNumber || "",
                title: d.title || "",
                ownershipShare: d.percentageOwnership || d.ownershipShare || "",
                bvn: d.bvnNumber || d.bvn || "",
                gender: d.gender || "",
                dateOfBirth: d.dateOfBirth || "",
                meansOfID: fromApiMeansOfId(d.meanOfIdentificationType || d.meansOfIdentification),
                identificationNumber:
                    d.idNumber || d.meanOfIdentificationNumber || d.identificationNumber || "",
                idDocumentFront: null,
                idDocumentFrontUrl:
                    d.idDocumentFront?.url ||
                    d.meanOfIdentificationUrl ||
                    d.identificationDocument?.url ||
                    "",
                idDocumentFrontKey:
                    d.idDocumentFront?.key ||
                    d.meanOfIdentificationKey ||
                    d.identificationDocument?.key ||
                    "",
                idDocumentBack: null,
                idDocumentBackUrl: d.idDocumentBack?.url || "",
                idDocumentBackKey: d.idDocumentBack?.key || "",
                streetAddress:
                    d.address?.street ||
                    d.residentialAddress?.street ||
                    d.residentialAddress?.streetAddress ||
                    d.streetAddress ||
                    "",
                city: d.address?.city || d.residentialAddress?.city || d.city || "",
                state: d.address?.state || d.residentialAddress?.state || d.state || "",
                postalCode:
                    d.address?.zipCode ||
                    d.residentialAddress?.postalCode ||
                    d.residentialAddress?.zipCode ||
                    d.postalCode ||
                    "",
            }));
            setDirectors(mappedDirectors);

            if (shouldReturnToSummary && mappedDirectors.length) {
                const firstDirector = mappedDirectors[0];
                setNewDirector({
                    ...firstDirector,
                    phoneNumber: String(firstDirector.phoneNumber || "").replace(/^\+/, ""),
                    gender: String(firstDirector.gender || "").toLowerCase(),
                    dateOfBirth: String(firstDirector.dateOfBirth || "").slice(0, 10),
                    idDocumentFront: firstDirector.idDocumentFrontUrl
                        ? {
                              name: firstDirector.idDocumentFrontKey || "Identification front",
                              size: 0,
                              url: firstDirector.idDocumentFrontUrl,
                              key: firstDirector.idDocumentFrontKey,
                          }
                        : null,
                    idDocumentBack: firstDirector.idDocumentBackUrl
                        ? {
                              name: firstDirector.idDocumentBackKey || "Identification back",
                              size: 0,
                              url: firstDirector.idDocumentBackUrl,
                              key: firstDirector.idDocumentBackKey,
                          }
                        : null,
                });
                setFrontDocumentUpload({
                    loading: false,
                    progress: 100,
                    uploaded: Boolean(firstDirector.idDocumentFrontUrl),
                });
                setBackDocumentUpload({
                    loading: false,
                    progress: 100,
                    uploaded: Boolean(firstDirector.idDocumentBackUrl),
                });
                setShowForm(true);
            } else if (!isUpdating) {
                // Open the form automatically only when the business has no directors yet.
                setShowForm(mappedDirectors.length === 0);
            }
        } catch (error) {
            console.error("Error fetching directors:", error);
        }
    }, [businessId, isUpdating, shouldReturnToSummary]);

    useEffect(() => {
      if (!isUpdating && businessId) {
            fetchDirectors();
      } else if (isUpdating && businessId && !prefillData) {
        fetchDirectors();
      }
    }, [businessId, fetchDirectors, isUpdating, prefillData]);

  useEffect(() => {
    if (!isUpdating || !prefillData) return;
    const mapped = mapDirectorToForm(
      prefillData,
      businessCountryCode,
      phoneCountryOptions
    );
    if (mapped) {
      setNewDirector(mapped);
      setShowForm(true);
      setFrontDocumentUpload({
        loading: false,
        progress: 100,
        uploaded: Boolean(mapped.idDocumentFrontUrl),
      });
      setBackDocumentUpload({
        loading: false,
        progress: 100,
        uploaded: Boolean(mapped.idDocumentBackUrl),
      });
        }
  }, [isUpdating, prefillData, businessCountryCode, phoneCountryOptions]);

    useEffect(() => {
        if (reduxBusinessCountryCode) {
            setBusinessCountryCode(reduxBusinessCountryCode);
        }
        if (reduxBusinessCountryName) {
            setBusinessCountryName(reduxBusinessCountryName);
        }
    }, [reduxBusinessCountryCode, reduxBusinessCountryName]);

    useEffect(() => {
        let ignore = false;

        const fetchCountries = async () => {
            setCountriesLoading(true);
            try {
                const responseBody = await handleFetchKybCountries();
                const countries = normalizeCountriesResponse(responseBody);
                const options = countries
                    .map((item) => {
                        const isoCode = String(item.code || "").toUpperCase();
                        const countryMeta = Country.getCountryByCode(isoCode);
                        const phonecode =
                            item.phoneCode ||
                            item.phonecode ||
                            item.phone?.[0] ||
                            countryMeta?.phonecode;

                        if (!isoCode || !phonecode) return null;

                        const dialCode = String(phonecode).startsWith("+")
                            ? String(phonecode)
                            : `+${phonecode}`;

                        return {
                            value: isoCode,
                            code: isoCode,
                            name: item.name,
                            dialCode,
                            searchLabel: `${item.name} ${isoCode} ${dialCode}`,
                            displayValue: isoCode,
                            label: (
                                <span className="flex w-full items-center gap-2">
                                    <img
                                        src={item.imageUrl}
                                        alt={`${item.name} flag`}
                                        className="size-5 rounded-full object-cover"
                                    />
                                    <span>{isoCode}</span>
                                    <span className="ml-auto text-sm text-[#6F739B]">
                                        {dialCode}
                                    </span>
                                </span>
                            ),
                        };
                    })
                    .filter(Boolean);

                if (!ignore) {
                    setPhoneCountryOptions(options);
                }
            } catch (error) {
                if (!ignore) {
                    setErrorMessage(
                        error?.message ||
                            "Unable to load phone countries. Please refresh and try again."
                    );
                }
            } finally {
                if (!ignore) setCountriesLoading(false);
            }
        };

        fetchCountries();

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (
            shouldReturnToSummary ||
            phoneCountryTouched ||
            (!businessCountryCode && !businessCountryName) ||
            !phoneCountryOptions.length
        )
            return;

        const defaultPhoneCountry =
            phoneCountryOptions.find(
                (option) =>
                    option.code === businessCountryCode ||
                    normalizeCountryName(option.name) === normalizeCountryName(businessCountryName)
            )?.code || "";

        if (!defaultPhoneCountry) return;

        setNewDirector((prev) =>
            prev.phoneCountry === defaultPhoneCountry
                ? prev
                : { ...prev, phoneCountry: defaultPhoneCountry }
        );
    }, [
        businessCountryCode,
        businessCountryName,
        phoneCountryOptions,
        phoneCountryTouched,
        shouldReturnToSummary,
    ]);

    useEffect(() => {
        if (!shouldReturnToSummary || !phoneCountryOptions.length) return;
        setNewDirector((prev) => {
            const fullPhone = String(prev.phoneNumber || "").replace(/\s/g, "");
            const matchingCountry = [...phoneCountryOptions]
                .sort((a, b) => b.dialCode.length - a.dialCode.length)
                .find((option) => fullPhone.startsWith(option.dialCode.replace(/^\+/, "")));
            if (!matchingCountry) return prev;
            return {
                ...prev,
                phoneCountry: matchingCountry.code,
                phoneNumber: fullPhone.slice(matchingCountry.dialCode.replace(/^\+/, "").length),
            };
        });
    }, [phoneCountryOptions, shouldReturnToSummary]);

    const selectedPhoneCountry = phoneCountryOptions.find(
        (option) => option.code === newDirector.phoneCountry
    );
    const requiresBvn = businessRequiresBvn;
    const needsBackDocument = newDirector.meansOfID !== "Passport";

    const getPhoneDialCode = () => selectedPhoneCountry?.dialCode || "";
    const getDefaultPhoneCountryCode = () =>
        phoneCountryOptions.find(
            (option) =>
                option.code === businessCountryCode ||
                normalizeCountryName(option.name) === normalizeCountryName(businessCountryName)
        )?.code ||
        (!phoneCountryOptions.length && businessCountryCode) ||
        "";

    const getUploadUrl = (raw) =>
        (typeof raw === "string" && raw) ||
        raw?.url ||
        raw?.url?.link ||
        raw?.link ||
        raw?.data?.url ||
        raw?.data?.url?.link ||
        raw?.data?.link ||
        "";

    const getUploadKey = (raw, fallback = "") =>
        raw?.key ||
        raw?.fileKey ||
        raw?.data?.key ||
        raw?.data?.fileKey ||
        raw?.path ||
        raw?.data?.path ||
        fallback;

    const buildDirectorPayload = () => {
        const cleanPhone = String(newDirector.phoneNumber || "").replace(/[^\d]/g, "");

        return {
            ...(requiresBvn && newDirector.bvn ? { bvn: newDirector.bvn } : {}),
            firstName: newDirector.firstName,
            lastName: newDirector.lastName,
            email: newDirector.email,
            phoneNumber: cleanPhone ? `${getPhoneDialCode()}${cleanPhone}` : "",
            title: newDirector.title,
            meanOfIdentificationType: toApiIdentificationType(newDirector.meansOfID),
            meanOfIdentificationUrl: newDirector.idDocumentFrontUrl,
            gender: newDirector.gender === "male" ? "Male" : "Female",
            dateOfBirth: newDirector.dateOfBirth,
            percentageOwnership: String(newDirector.ownershipShare),
            idNumber: newDirector.identificationNumber,
            address: {
                houseNumber: extractHouseNumber(newDirector.streetAddress),
                street: newDirector.streetAddress,
                city: newDirector.city,
                state: newDirector.state,
                zipCode: newDirector.postalCode,
                subdivision: "",
            },
            idDocumentFront: {
                url: newDirector.idDocumentFrontUrl,
                key: newDirector.idDocumentFrontKey,
            },
            ...(needsBackDocument && newDirector.idDocumentBackUrl
                ? {
                      idDocumentBack: {
                          url: newDirector.idDocumentBackUrl,
                          key: newDirector.idDocumentBackKey,
                      },
                  }
                : {}),
        };
    };

  useEffect(() => {
    if (!isUpdating || typeof handleUpdateMoreData !== "function") return;

    if (isFormFilled(newDirector) && !ownershipHasError && !ownershipExceedsLimit) {
      const payload = buildDirectorPayload();
      handleUpdateMoreData("director", payload);
    } else {
      handleUpdateMoreData("director", {});
    }
  }, [isUpdating, newDirector, ownershipHasError, ownershipExceedsLimit]);

    const resetDocumentUploadState = () => {
        setFrontDocumentUpload({ loading: false, progress: 0, uploaded: false });
        setBackDocumentUpload({ loading: false, progress: 0, uploaded: false });
    };

    const handleAddDirector = async () => {
        if (!isFormFilled(newDirector) || ownershipHasError) return;

        if (ownershipExceedsLimit) {
            setErrorMessage("Total director ownership cannot exceed 100%.");
            return;
        }

        try {
          setSaving(true);
          const payload = buildDirectorPayload();
            await handleAddBusinessDirector(businessId, payload);
            setErrorMessage("");
            await fetchDirectors();

            setPhoneCountryTouched(false);
            setNewDirector({ ...initialDirectorForm, phoneCountry: getDefaultPhoneCountryCode() });
            resetDocumentUploadState();
            setShowForm(false);
            if (shouldReturnToSummary) {
                navigate("/onboarding/summary");
            }
        } catch (error) {
            console.error("Failed to add director:", error);
            const message = error?.message || "Something went wrong. Please try again.";
            setErrorMessage(message);
        } finally {
            setSaving(false);
        }
    };

    const handleAddAndContinue = async () => {
        if (!isFormFilled(newDirector) || ownershipHasError) return;

        if (ownershipExceedsLimit) {
            setErrorMessage("Total director ownership cannot exceed 100%.");
            return;
        }

        try {
            const payload = buildDirectorPayload();

            await handleAddBusinessDirector(businessId, payload);
            await fetchDirectors();
            setPhoneCountryTouched(false);
            setNewDirector({ ...initialDirectorForm, phoneCountry: getDefaultPhoneCountryCode() });
            resetDocumentUploadState();

            setShowForm(true);
            setErrorMessage("");
            if (shouldReturnToSummary) {
                navigate("/onboarding/summary");
            }
        } catch (error) {
            console.error("Failed to add director:", error);
            const message = error?.message || "Something went wrong. Please try again.";
            setErrorMessage(message);
        }
    };

    const handleChange = (field, value) => {
        setNewDirector((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "phoneCountry" && !businessRequiresBvn ? { bvn: "" } : {}),
            ...(field === "meansOfID"
                ? {
                      idDocumentFront: null,
                      idDocumentFrontUrl: "",
                      idDocumentFrontKey: "",
                      idDocumentBack: null,
                      idDocumentBackUrl: "",
                      idDocumentBackKey: "",
                  }
                : {}),
        }));

        if (field === "meansOfID") {
            resetDocumentUploadState();
        }

        if (field === "bvn" || field === "phoneCountry") {
            const nextBvn =
                field === "phoneCountry" && !businessRequiresBvn
                    ? ""
                    : field === "bvn"
                      ? value
                      : newDirector.bvn;

            if (businessRequiresBvn && nextBvn && !isValidBvn(nextBvn)) {
                setIdentificationError("BVN must be exactly 11 digits.");
                return;
            }

            setIdentificationError("");
        }

        if (field === "ownershipShare") {
            setErrorMessage((prev) =>
                prev?.includes("Total director ownership cannot exceed 100%") ? "" : prev
            );
        }
    };

    const handleFileUpload = async (event, side = "front") => {
        const file = event.target.files?.[0];
        if (!file) return;

        const isBack = side === "back";
        const setUploadState = isBack ? setBackDocumentUpload : setFrontDocumentUpload;
        const documentField = isBack ? "idDocumentBack" : "idDocumentFront";
        const documentUrlField = isBack ? "idDocumentBackUrl" : "idDocumentFrontUrl";
        const documentKeyField = isBack ? "idDocumentBackKey" : "idDocumentFrontKey";

        setUploadState({ loading: true, progress: 0, uploaded: false });
        setNewDirector((prev) => ({ ...prev, [documentField]: file }));

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            setUploadState((prev) => ({
                ...prev,
                progress: Math.min(progress, 95),
            }));
            if (progress >= 95) clearInterval(interval);
        }, 100);

        try {
            const response = await handleUploadOnboardingDocument(file);
            const uploadData = response?.data || response;
            const url = getUploadUrl(uploadData);

            if (!url) {
                throw new Error(response?.message || "No upload URL returned");
            }

            setNewDirector((prev) => ({
                ...prev,
                [documentField]: {
                    name: file.name,
                    size: file.size,
                    url,
                    key: getUploadKey(uploadData, file.name),
                },
                [documentUrlField]: url,
                [documentKeyField]: getUploadKey(uploadData, file.name),
            }));
            setUploadState({ loading: false, progress: 100, uploaded: true });
            setErrorMessage("");
        } catch (error) {
            console.error("File upload failed:", error?.response?.data || error.message);
            setNewDirector((prev) => ({
                ...prev,
                [documentField]: null,
                [documentUrlField]: "",
                [documentKeyField]: "",
            }));
            setUploadState({ loading: false, progress: 0, uploaded: false });
            setErrorMessage("Upload failed. Please try again with a valid document.");
        } finally {
            clearInterval(interval);
            event.target.value = "";
        }
    };

    const handleRemoveFile = (side = "front") => {
        const isBack = side === "back";
        const documentField = isBack ? "idDocumentBack" : "idDocumentFront";
        const documentUrlField = isBack ? "idDocumentBackUrl" : "idDocumentFrontUrl";
        const documentKeyField = isBack ? "idDocumentBackKey" : "idDocumentFrontKey";
        const setUploadState = isBack ? setBackDocumentUpload : setFrontDocumentUpload;

        setNewDirector((prev) => ({
            ...prev,
            [documentField]: null,
            [documentUrlField]: "",
            [documentKeyField]: "",
        }));
        setUploadState({ loading: false, progress: 0, uploaded: false });
    };

    const handleFinalSubmission = async () => {
        navigate("/onboarding/summary");
    };

    const isFormFilled = (d) => {
        const bvnIsValid = !requiresBvn || (d.bvn && isValidBvn(d.bvn));

        return (
            d.firstName &&
            d.lastName &&
            d.email &&
            d.phoneNumber &&
            d.title &&
            isOwnershipShareValid(d.ownershipShare) &&
            bvnIsValid &&
            d.gender &&
            d.dateOfBirth &&
            d.meansOfID &&
            d.identificationNumber &&
            d.idDocumentFrontUrl &&
            (d.meansOfID === "Passport" || d.idDocumentBackUrl) &&
            d.streetAddress &&
            d.city &&
            d.state &&
            d.postalCode
        );
    };

    const hasAtLeastOneSavedDirector =
        directors.length > 0 &&
        directors.some((d) => d.firstName && d.lastName && d.meansOfID && d.idDocumentFrontUrl);

    const isAddingFirstDirector = directors.length === 1 && !directors[0].idDocumentFrontUrl;

  const Wrapper = isUpdating ? Fragment : OnboardingLayout;
  const wrapperProps = isUpdating
    ? {}
    : {
      title: "Activate account",
      steps: steps,
      currentStep: currentStep,
      currentSubStep: 1,
      isUpdating: isUpdating,
    };

    return (
      <Wrapper {...wrapperProps}>
          <div className={cn("max-w-[1200px] relative justify-center", !isUpdating ? "md:pl-96" : "")}>
            {!isUpdating && <h3 className="aku-heading-h2-medium text-[#101828]">Add directors</h3>}

            <div className={cn("space-y-4", !isUpdating ? "mt-8" : "mt-0")}>
                        {/* Preview already added directors (excluding last one) */}
              {!isAddingFirstDirector &&
                            directors
                                .filter((d) => d.idDocumentFrontUrl)
                                .map((director, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-4 rounded-lg bg-white"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="size-10 rounded-full bg-[#F9F5FF] flex items-center justify-center text-[#7F56D9] font-semibold">
                                                {director.firstName && director.lastName ? (
                                                    `${director.firstName
                                                        .charAt(0)
                                                        .toUpperCase()}${director.lastName
                                                        .charAt(0)
                                                        .toUpperCase()}`
                                                ) : (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-5 w-5"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        strokeWidth={1.5}
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M4.501 19.5a7.5 7.5 0 0114.998 0"
                                                        />
                                                    </svg>
                                                )}
                                            </div>

                                            <div>
                                                <p className="font-semibold text-[#101828]">
                                                    {director.firstName && director.lastName
                                                        ? `Director ${index + 1}: ${
                                                              director.firstName
                                                          } ${director.lastName}`
                                                        : `Director ${index + 1}`}
                                                </p>
                                                {requiresBvn && director.bvn && (
                                                    <p className="text-sm text-[#667085]">
                                                        {director.bvn}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                        {/* Form for current/last director */}
                        {showForm && (
                            <div className="rounded-lg bg-white !p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                      <p className={cn("font-normal text-[#101828]", !isUpdating ? "!text-lg" : "text-sm")}>
                                            Director{" "}
                                            {directors.filter((d) => d.idDocumentFrontUrl).length +
                                                1}
                                        </p>
                                    </div>

                    {!isUpdating && (<button
                                        onClick={() => setShowForm(false)}
                                        className="text-gray-400 hover:text-red-500"
                                        title="Cancel"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                    </button>)}
                                </div>

                                {errorMessage && <ErrorMessage message={errorMessage} />}

                                <div className="mt-4">
                                    <p className="aku-body-small-medium mb-2 text-[#383B56]">Name</p>
                                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                        <Input
                                            type="text"
                                            value={newDirector.firstName}
                                            onChange={(value) => handleChange("firstName", value)}
                                            placeholder="First name"
                                            className="!mt-0"
                                        />
                                        <Input
                                            type="text"
                                            value={newDirector.lastName}
                                            onChange={(value) => handleChange("lastName", value)}
                                            placeholder="Last name"
                                            className="!mt-0"
                                        />
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <Input
                                        type="email"
                                        label="Email"
                                        labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                        value={newDirector.email}
                                        onChange={(value) => handleChange("email", value)}
                                        placeholder="Enter email address"
                                        className="!mt-0"
                                    />
                                </div>

                                <div className="mt-4">
                                    <p className="aku-body-small-medium mb-2 text-[#383B56]">
                                        Phone number
                                    </p>
                                    <div className="flex gap-2">
                                        <div className="w-[104px] shrink-0">
                                            <Dropdown
                                                value={newDirector.phoneCountry}
                                                onChange={(value) => {
                                                    setPhoneCountryTouched(true);
                                                    handleChange("phoneCountry", value);
                                                }}
                                                options={phoneCountryOptions}
                                                className="!mt-0"
                                                dropdownClassName="!min-w-[104px]"
                                                searchable
                                                searchPlaceholder="Search country"
                                                placeholder={countriesLoading ? "..." : "--"}
                                                readOnly={countriesLoading}
                                            />
                                        </div>
                                        <div className="flex h-[56px] min-w-0 flex-1 items-center rounded-lg bg-[#f1f5f9] px-4">
                                            <span className="aku-body-normal-medium mr-3 text-[#383B56]">
                                                {getPhoneDialCode()}
                                            </span>
                                            <input
                                                type="tel"
                                                value={newDirector.phoneNumber}
                                                onChange={(event) =>
                                                    handleChange(
                                                        "phoneNumber",
                                                        event.target.value.replace(/[^\d]/g, "")
                                                    )
                                                }
                                                placeholder="905 555 0123"
                                                className="aku-body-normal-regular min-w-0 flex-1 bg-transparent text-main-dark placeholder:text-main-grey outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                                    <div>
                                        <Input
                                            label="Title"
                                            labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                            value={newDirector.title}
                                            onChange={(value) => handleChange("title", value)}
                                            placeholder="e.g. CEO, MD, CFO, etc."
                                            className="!mt-0"
                                        />
                                    </div>
                                    <div>
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            label="Ownership share (in %)"
                                            labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                            value={newDirector.ownershipShare}
                                            onChange={(value) =>
                                                handleChange(
                                                    "ownershipShare",
                                                    normalizeOwnershipInput(value)
                                                )
                                            }
                                            placeholder="25.00 %"
                                            inputClassName={
                                                ownershipHasError
                                                    ? "!bg-[#FFF1F3] !shadow-[inset_0_0_0_1000px_#FFF1F3] !ring-1 !ring-red-400"
                                                    : ""
                                            }
                                            className="!mt-0"
                                        />
                                        {ownershipErrorMessage ? (
                                            <p className="mt-1 text-xs text-red-500">
                                                {ownershipErrorMessage}
                                            </p>
                                        ) : (
                                            <p className="mt-1 text-xs text-[#667085]">
                                                Ownership share must be between 25% and 100%.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {requiresBvn && (
                                    <div className="mt-4">
                                        <Input
                                            type="text"
                                            label="Bank verification number (BVN)"
                                            labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                            value={newDirector.bvn}
                                            onChange={(value) => handleChange("bvn", value)}
                                            placeholder="e.g. 0864297531"
                                            className="!mt-0"
                                        />
                                        {identificationError && (
                                            <p className="mt-1 text-sm text-red-500">
                                                {identificationError}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
                                    <div>
                                        <p className="aku-body-small-medium mb-2 text-[#383B56]">
                                            Gender
                                        </p>
                                        <Dropdown
                                            value={newDirector.gender}
                                            onChange={(value) => handleChange("gender", value)}
                                            options={genderOptions}
                                            placeholder="Select gender"
                                            className="!mt-0"
                                        />
                                    </div>
                                    <div>
                                        <Input
                                            type="date"
                                            label="Date of birth"
                                            labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                            value={newDirector.dateOfBirth}
                                            onChange={(value) => handleChange("dateOfBirth", value)}
                                            placeholder="Select date"
                                            min="1900-01-01"
                                            max={getMaxDateOfBirth()}
                                            className="!mt-0"
                                        />
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div className="mb-2 flex items-center justify-between">
                                        <p className="aku-body-small-medium text-[#101828]">
                                            Identification document
                                        </p>
                                        <button
                                            type="button"
                                            className="aku-body-normal-medium flex items-center gap-1 text-[#6F00FF]"
                                            onClick={() => setIsSampleModalOpen(true)}
                                        >
                                            See sample{" "}
                                            <span className="material-icons">chevron_right</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                        <Dropdown
                                            value={newDirector.meansOfID}
                                            onChange={(value) => handleChange("meansOfID", value)}
                                            placeholder="Select ID type"
                                            options={meansOfIDOptions}
                                            className="!mt-0"
                                        />
                                        <Input
                                            value={newDirector.identificationNumber}
                                            onChange={(value) =>
                                                handleChange("identificationNumber", value)
                                            }
                                            placeholder={`Enter ${newDirector.meansOfID.toLowerCase()} ID number`}
                                            className="!mt-0"
                                        />
                                    </div>

                                    <div
                                        className={`mt-2 grid grid-cols-1 gap-2 ${
                                            needsBackDocument ? "md:grid-cols-2" : ""
                                        }`}
                                    >
                                        <UploadInputField
                                            label={
                                                needsBackDocument
                                                    ? "Upload front side"
                                                    : "Upload passport"
                                            }
                                            uploadTitle={
                                                needsBackDocument
                                                    ? "Upload front side"
                                                    : `Upload ${newDirector.meansOfID.toLowerCase()}`
                                            }
                                            hideLabel
                                            fileKey="director-identification-document-front"
                                            fileState={{
                                                file: newDirector.idDocumentFront,
                                                loading: frontDocumentUpload.loading,
                                                uploaded: frontDocumentUpload.uploaded,
                                                progress: frontDocumentUpload.progress,
                                            }}
                                            onFileChange={(event) =>
                                                handleFileUpload(event, "front")
                                            }
                                            onRemove={() => handleRemoveFile("front")}
                                        />
                                        {needsBackDocument && (
                                            <UploadInputField
                                                label="Upload back side"
                                                uploadTitle="Upload back side"
                                                hideLabel
                                                fileKey="director-identification-document-back"
                                                fileState={{
                                                    file: newDirector.idDocumentBack,
                                                    loading: backDocumentUpload.loading,
                                                    uploaded: backDocumentUpload.uploaded,
                                                    progress: backDocumentUpload.progress,
                                                }}
                                                onFileChange={(event) =>
                                                    handleFileUpload(event, "back")
                                                }
                                                onRemove={() => handleRemoveFile("back")}
                                            />
                                        )}
                                    </div>

                                    <div className="mt-2 flex items-center gap-2 text-[#475467]">
                                        <InfoCircle size="20" color="#2C53F2" variant="Bold" />
                                        <p className="aku-body-small-regular">
                                            All information on the {needsBackDocument ? "documents" : "document"} should be clear and readable
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <Input
                                        type="textarea"
                                        label="Residential address"
                                        labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                        value={newDirector.streetAddress}
                                        onChange={(value) => handleChange("streetAddress", value)}
                                        placeholder="Street address"
                                        className="!mt-0"
                                    />
                                    <div className="mt-6 grid grid-cols-1 gap-2 md:grid-cols-3">
                                        <Input
                                            value={newDirector.city}
                                            onChange={(value) => handleChange("city", value)}
                                            placeholder="City"
                                            className="!mt-0"
                                        />
                                        <Input
                                            value={newDirector.state}
                                            onChange={(value) => handleChange("state", value)}
                                            placeholder="State / Province"
                                            className="!mt-0"
                                        />
                                        <Input
                                            value={newDirector.postalCode}
                                            onChange={(value) => handleChange("postalCode", value)}
                                            placeholder="Postal / ZIP Code"
                                            className="!mt-0"
                                        />
                                    </div>
                                </div>

                  {!isUpdating && (
                    <button
                      className={`!mt-8 !px-5 !py-3 rounded-md text-sm font-semibold ${saving || !isFormFilled(newDirector) || ownershipHasError
                          ? "bg-[#E9D7FE] text-white cursor-not-allowed"
                          : "bg-[#7F56D9] text-white hover:bg-[#6f00ff]"
                        }`}
                      onClick={handleAddDirector}
                      disabled={
                        saving || !isFormFilled(newDirector) || ownershipHasError
                      }
                    >
                      {saving ? <Loader /> : "Save"}
                    </button>
                  )}
                            </div>
                        )}

                        {/* Add Director Button */}
                        <SampleDocumentModal
                            isOpen={isSampleModalOpen}
                            onClose={() => setIsSampleModalOpen(false)}
                            title={`${newDirector.meansOfID} sample`}
                            tabs={[]}
                            documentSrc={identificationSampleDocuments[newDirector.meansOfID]}
                        />

              {!isUpdating && <button
                            type="button"
                            onClick={() => {
                                if (!showForm) {
                                    setShowForm(true);
                                } else {
                                    handleAddAndContinue();
                                }
                            }}
                            disabled={showForm && (!isFormFilled(newDirector) || ownershipHasError)}
                            className={`text-[#7F56D9] !mt-6 text-sm font-medium hover:underline flex items-center gap-2 ${
                                showForm && (!isFormFilled(newDirector) || ownershipHasError)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                            }`}
                        >
                            + Add another director
              </button>}

                        {/* Navigation Buttons */}
              {!isUpdating && <div className="flex justify-end gap-4 pt-4 !mb-24">
                            <button
                                type="button"
                                onClick={() =>
                                    onBack
                                        ? onBack()
                                        : navigate("/onboarding/business-ownership/control-person")
                                }
                                className="rounded-lg px-6 py-3 text-sm font-semibold text-[#101828]"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={handleFinalSubmission}
                                disabled={!hasAtLeastOneSavedDirector}
                                className={`rounded-lg px-6 py-3 text-sm font-semibold ${
                                    !hasAtLeastOneSavedDirector
                                        ? "bg-[#E9D7FE] text-white cursor-not-allowed"
                                        : "bg-[#7F56D9] text-white"
                                }`}
                            >
                                Complete activation
                            </button>
              </div>}
                    </div>
                </div>
      </Wrapper>
    );
};

export default AddDirectors;
